/**
 * useUserPrefsHydration.ts -- MER Algo Footprints v9.2.0
 *
 * Phase 3: hydrates user prefs from Postgres into the Zustand store
 * whenever the auth status transitions to 'authenticated'.
 *
 * Hydrated prefs:
 *   - horizon       (via /api/user/prefs)
 *   - watchlist     (via /api/watchlist) [Backlog #10 Phase 2]
 *
 * Why a hook (and not a useEffect inside AuthContext):
 *   - AuthContext stays pref-agnostic. New prefs land here, not there.
 *   - The hook can be mounted inside AppShell (already auth-gated by
 *     ProtectedRoute) so hydration only runs for authenticated users
 *     -- it never fires on the login/signup pages.
 *   - Easy to test in isolation if/when we add tests later.
 *
 * Horizon behavior (G-CLIENT-2 / D-GCLIENT2-2b):
 *   - On mount, if auth is 'authenticated', fetch and apply on SUCCESS only.
 *   - On every transition from non-authenticated to 'authenticated',
 *     fetch and apply on success (e.g. after explicit login).
 *   - On 'unauthenticated', does nothing -- the localStorage value
 *     remains as the last known good.
 *   - If the fetch SUCCEEDS with the default (no persisted row), we still
 *     write to the store -- the store action is a no-op when unchanged.
 *   - If the fetch FAILS (network/auth/server/contract), the failure is an
 *     explicit error condition: the store is NOT overwritten, the last-known
 *     local value (or the intraday/5min UX default from store initialization)
 *     keeps rendering, and the failure is surfaced to diagnostics. A failed
 *     hydration is never misrepresented as successful preference hydration.
 *
 * Watchlist behavior (Backlog #10 Phase 2):
 *   - On auth, fetch the user's server-side watchlist.
 *   - Migration logic (server-wins with local-fallback):
 *       * Server returns null (unreachable):  leave local state alone.
 *       * Server returns non-empty array:     overwrite local via setWatchlist.
 *       * Server returns empty array AND local has symbols:
 *           push each local symbol to server (one POST per symbol).
 *           This preserves the user's pre-server-sync watchlist when
 *           they first log in after this change rolls out.
 *       * Server returns empty array AND local is empty:  nothing to do.
 *   - Mutation sync (add/remove) happens in store actions, not here.
 */

import { useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useStore } from '../store';
import { fetchHorizon } from '../data/userPrefsApi';
import { fetchWatchlist, addWatchlistSymbol } from '../data/watchlistApi';

export function useUserPrefsHydration(): void {
  const { status }      = useAuth();
  const setHorizon      = useStore(s => s.setHorizon);
  const setWatchlist    = useStore(s => s.setWatchlist);

  useEffect(() => {
    if (status !== 'authenticated') return;

    let cancelled = false;
    (async () => {
      // ─── Horizon ─────────────────────────────────────────────────────────
      const result = await fetchHorizon();   // never throws; ok discriminates
      if (cancelled) return;
      if (result.ok) {
        setHorizon(result.horizon);
      } else {
        // Explicit error condition — never applied as hydrated truth. The UX
        // continues on the last-known local value / intraday default.
        console.warn('[prefs] horizon hydration failed — keeping last known/default horizon');
      }

      // ─── Watchlist (Backlog #10 Phase 2) ─────────────────────────────────
      const serverSymbols = await fetchWatchlist();   // never throws; null on failure
      if (cancelled) return;

      if (serverSymbols === null) {
        // Server unreachable. Leave local state alone; mutations will retry sync.
        return;
      }

      if (serverSymbols.length > 0) {
        // Server has symbols -- they win.
        setWatchlist(serverSymbols);
        return;
      }

      // Server has empty watchlist. If local has symbols, push them up so the
      // user doesn't lose their pre-sync watchlist on first login.
      const localSymbols = useStore.getState().watchlist.map(w => w.symbol);
      if (localSymbols.length === 0) return;

      // Push each symbol. 409 (duplicate) is treated as success by the API
      // wrapper, so this is safe even if another tab beat us to it.
      for (const sym of localSymbols) {
        if (cancelled) return;
        try {
          await addWatchlistSymbol(sym);
        } catch {
          // Silent. A failed push just means this symbol stays local-only for
          // now; the next add/remove from the user will re-attempt sync.
        }
      }
    })();

    return () => { cancelled = true; };
  }, [status, setHorizon, setWatchlist]);
}
